import React, { useState, useContext } from "react";
import { graphql, useStaticQuery } from "gatsby";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlayCircle } from "@fortawesome/free-solid-svg-icons";
import { cn, mapEdgesToNodes, toPlainText, imageBuilder } from "../../lib/helpers";
import { pageDocPath } from "../../../helpers";
import { ImageBuilder } from "../global/image-builder";
import { SiteContext } from "../global/site-context";
import { SectionContext } from "./context";

const INITIAL_COUNT = 6;
const AVATAR_SIZE = 20;
const THUMBNAIL_WIDTH = 200;
const THUMBNAIL_HEIGHT = 113;

export const RecordingPlaylist = ({ recordings: recordingsProp, section }) => {
  const [showAll, setShowAll] = useState(false);
  const [activeCategory, setActiveCategory] = useState(null);
  const { isDarkBg: contextIsDarkBg } = useContext(SectionContext);
  const isDarkBg = section?.isDarkBg || contextIsDarkBg;
  const { generateUnpublishedPages } = useContext(SiteContext);

  const { invites } = useStaticQuery(recordingPlaylistGraphQL);

  const allInvites = mapEdgesToNodes(invites);
  const recordings =
    recordingsProp ||
    (generateUnpublishedPages
      ? allInvites
      : allInvites?.filter((d) => d.status === "published"));

  const sorted = [...recordings]
    .filter((node) => !node.startDate || new Date(node.startDate) >= new Date("2024-01-01"))
    .sort((a, b) => {
      if (!a.startDate) return 1;
      if (!b.startDate) return -1;
      return new Date(b.startDate) - new Date(a.startDate);
    });

  const featuredCategories = [
    ...new Map(
      sorted
        .flatMap((node) => node.categories || [])
        .filter((c) => c.isFeatured && c.status === "published")
        .map((c) => [c._id, c])
    ).values(),
  ];

  const filtered = activeCategory
    ? sorted.filter((node) => node.categories?.some((c) => c._id === activeCategory))
    : sorted;

  const visible = showAll ? filtered : filtered.slice(0, INITIAL_COUNT);

  const handleCategoryClick = (id) => {
    setActiveCategory(id);
    setShowAll(false);
  };

  return (
    <div>
      {featuredCategories.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-6">
          <button
            onClick={() => handleCategoryClick(null)}
            className={cn(
              "px-3 py-1 rounded-full text-sm font-medium transition-colors duration-150",
              !activeCategory
                ? isDarkBg ? "bg-light-20 text-white" : "bg-dark-10 text-primary"
                : isDarkBg ? "bg-light-10 text-light-60 hover:bg-light-20 hover:text-white" : "bg-dark-5 text-secondary hover:bg-dark-10 hover:text-primary"
            )}
          >
            All
          </button>
          {featuredCategories.map((cat) => (
            <button
              key={cat._id}
              onClick={() => handleCategoryClick(cat._id)}
              className={cn(
                "px-3 py-1 rounded-full text-sm font-medium transition-colors duration-150",
                activeCategory === cat._id
                  ? isDarkBg ? "bg-light-20 text-white" : "bg-dark-10 text-primary"
                  : isDarkBg ? "bg-light-10 text-light-60 hover:bg-light-20 hover:text-white" : "bg-dark-5 text-secondary hover:bg-dark-10 hover:text-primary"
              )}
            >
              {cat.titlePublic || cat.title}
            </button>
          ))}
        </div>
      )}
      <div className={cn("divide-y", isDarkBg ? "divide-light-10" : "divide-dark-5")}>
        {visible.map((node) => {
          const summary = toPlainText(node._rawExcerpt);
          const speakers = node.authors
            ?.map((item) => ({
              name: item.name || item.author?.name,
              image: item.author?.image,
            }))
            .filter((speaker) => speaker.name);

          return (
            <a
              key={node._id}
              href={pageDocPath(node)}
              className={cn(
                "flex flex-col-reverse sm:flex-row items-start gap-4 py-6 px-4 rounded-lg group transition-colors duration-150",
                isDarkBg ? "hover:bg-light-10" : "hover:bg-white"
              )}
            >
              <span className="min-w-0 flex-1 flex flex-col gap-1">
                <span className="block font-medium text-xl">{node.title}</span>
                {summary && (
                  <span className={cn("block text-sm md:max-w-2/3 leading-snug", isDarkBg ? "text-light-60" : "text-secondary")}>
                    {summary}
                  </span>
                )}
                {speakers?.length > 0 && (
                  <span className={cn("flex flex-wrap items-center gap-x-3 gap-y-1 text-xs mt-0.5", isDarkBg ? "text-light-40" : "text-auxiliary")}>
                    {speakers.map((speaker, i) => (
                      <span key={`${node._id}-speaker-${i}`} className="inline-flex items-center gap-1.5">
                        {speaker.image?.asset && (
                          <ImageBuilder
                            src={imageBuilder(speaker.image)
                              .width(AVATAR_SIZE * 2)
                              .height(AVATAR_SIZE * 2)
                              .quality(100)
                              .auto("format")
                              .fit("crop")
                              .url()}
                            alt={speaker.name}
                            width={AVATAR_SIZE}
                            height={AVATAR_SIZE}
                            className="rounded-full shrink-0"
                          />
                        )}
                        {speaker.name}
                      </span>
                    ))}
                  </span>
                )}
              </span>
              <span className="relative flex-shrink-0 rounded-md overflow-hidden bg-dark-5 w-full sm:w-200">
                <span
                  className="block"
                  style={{ paddingBottom: `${(THUMBNAIL_HEIGHT / THUMBNAIL_WIDTH) * 100}%`, height: 0 }}
                />
                <span className="absolute inset-0">
                  {node.mainImage?.asset && (
                    <ImageBuilder
                      src={imageBuilder(node.mainImage)
                        .width(THUMBNAIL_WIDTH * 4)
                        .height(THUMBNAIL_HEIGHT * 4)
                        .quality(100)
                        .auto("format")
                        .fit("crop")
                        .url()}
                      alt={node.title}
                      width={THUMBNAIL_WIDTH}
                      height={THUMBNAIL_HEIGHT}
                      className="w-full h-full object-cover"
                    />
                  )}
                  <FontAwesomeIcon
                    icon={faPlayCircle}
                    className="absolute inset-0 m-auto text-white opacity-80 group-hover:opacity-100 text-3xl transition-opacity duration-150 h-8 w-8 drop-shadow"
                  />
                </span>
              </span>
            </a>
          );
        })}
      </div>
      {!showAll && filtered.length > INITIAL_COUNT && (
        <button
          onClick={() => setShowAll(true)}
          className={cn(
            "mt-4 w-full py-3 text-sm font-medium transition-colors duration-150",
            isDarkBg ? "text-light-60 hover:text-white" : "text-secondary hover:text-primary"
          )}
        >
          Show all {filtered.length} recordings
        </button>
      )}
    </div>
  );
};

const recordingPlaylistGraphQL = graphql`
  query RecordingPlaylistQuery {
    invites: allSanityInvite(
      limit: 50
      sort: { fields: [startDate], order: [DESC] }
      filter: {
        eventType: { eq: "on-demand" }
        mainVideo: { url: { ne: null } }
      }
    ) {
      edges {
        node {
          _type
          _id
          status
          title
          _rawExcerpt
          startDate
          slug { current }
          mainImage {
            crop {
              _key
              _type
              top
              bottom
              left
              right
            }
            hotspot {
              _key
              _type
              x
              y
              height
              width
            }
            asset {
              _id
            }
          }
          categories {
            _id
            title
            titlePublic
            isFeatured
            status
          }
          authors {
            ... on SanityAuthorReference {
              _key
              _type
              author {
                name
                image {
                  crop {
                    _key
                    _type
                    top
                    bottom
                    left
                    right
                  }
                  hotspot {
                    _key
                    _type
                    x
                    y
                    height
                    width
                  }
                  asset {
                    _id
                  }
                }
              }
            }
            ... on SanitySpeakerName {
              _key
              _type
              name
            }
          }
        }
      }
    }
  }
`;
